import { useFirebase } from '@chem-po/firebase-web'
import { UsePracticeContractsStatus } from '@hb/react'
import { MIDWIVES, objectToArray, PRACTICE_CONTRACTS, PracticeContract, WithId } from '@hb/shared'
import { collection, query, Query, where } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '../backend/useQuery'
import { useSignPracticeContract } from './useSignPracticeContract'

// Derives a practice's contract list directly from the live subcollection query
// (no denormalized status map). Optionally filters to a single signer (provider).
export const usePracticeContractsStatus = (
  practiceId: string | null,
  signerId?: string | null,
): UsePracticeContractsStatus => {
  const { firestore } = useFirebase()
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null)

  const contractsQuery = useMemo(() => {
    if (!practiceId) return null
    const contractsCollection = collection(firestore, MIDWIVES, practiceId, PRACTICE_CONTRACTS)
    return (
      signerId
        ? query(contractsCollection, where('toBeSignedByUser', '==', signerId))
        : query(contractsCollection)
    ) as Query<PracticeContract>
  }, [practiceId, signerId, firestore])
  const { data: contractsMap } = useQuery(contractsQuery)

  const contracts = useMemo<WithId<PracticeContract>[]>(() => {
    const all = objectToArray(contractsMap ?? {}) as WithId<PracticeContract>[]
    return all.sort((a, b) => b.sentOn - a.sentOn)
  }, [contractsMap])

  const handleViewContract = useCallback((contractId: string) => {
    setSelectedContractId(contractId)
  }, [])

  const handleCloseModal = useCallback(() => {
    setSelectedContractId(null)
  }, [])

  const selectedContract = useMemo<WithId<PracticeContract> | null>(() => {
    if (!selectedContractId) return null
    const contract = contractsMap?.[selectedContractId]
    if (!contract) return null
    return { id: selectedContractId, ...contract }
  }, [contractsMap, selectedContractId])

  const { onSignContract, canDownload } = useSignPracticeContract(selectedContract)

  const numContracts = contracts.length
  const numSigned = useMemo(() => contracts.filter(c => !!c.signedOn).length, [contracts])

  return {
    contracts,
    selectedContract,
    handleViewContract,
    handleCloseModal,
    onSignContract,
    canDownload,
    numContracts,
    numSigned,
  }
}
